import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { GameTypeTabs } from './components/GameTypeTabs';
import { StatTabs } from './components/StatTabs';
import { TopSection } from './components/TopSection';
import { MobileTopSection } from './components/MobileTopSection';
import { FullRankingList } from './components/FullRankingList';
import { useBreakpoint } from './hooks/useBreakpoint';
import { useSports } from '../context/SportsProvider';
import WCBackground from '../components/WCBackground';
import styles from './WCStatistics.module.scss';
import { useSearchParams } from 'react-router-dom';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { useMundialMetrics } from '../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../hooks/useMundialMetrics/constants';
import { useMundialScrollTracker } from '../hooks/useMundialScrollTracker';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getScrollableParent = node => {
  if (!node || node === document.documentElement)
    return document.documentElement;
  const { overflowY } = window.getComputedStyle(node);
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
  if (isScrollable && node.scrollHeight > node.clientHeight) return node;
  return getScrollableParent(node.parentElement);
};

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const GAME_TYPE_DEFS = [
  {
    key: 'ofensiva',
    translationKey: 'eventodeportivo_estadisticajuegos_tabs_texto_tab1',
  },
  {
    key: 'defensiva',
    translationKey: 'eventodeportivo_estadisticajuegos_tabs_texto_tab2',
  },
  {
    key: 'porteria',
    translationKey: 'eventodeportivo_estadisticajuegos_tabs_texto_tab3',
  },
  {
    key: 'faltas',
    translationKey: 'eventodeportivo_estadisticajuegos_tabs_texto_tab4',
  },
];

const STATS_BY_GAME_TYPE = {
  ofensiva: [
    {
      key: 'goals',
      abbreviation: 'G',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_topgoles',
    },
    {
      key: 'assists',
      abbreviation: 'PA',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_toppasesgol',
    },
    {
      key: 'total_shots',
      abbreviation: 'TT',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_toptirostotales',
    },
    {
      key: 'shots_on_target',
      abbreviation: 'TAP',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_toptirosporteria',
    },
    {
      key: 'dribbles',
      abbreviation: 'RE',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_topregates',
    },
    {
      key: 'total_passes',
      abbreviation: 'PT',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_toppases',
    },
    {
      key: 'offsides',
      abbreviation: 'FJ',
      titleKey:
        'eventodeportivo_estadisticajuegos_ofensiva_texto_titulo_topfueradejuego',
    },
  ],
  defensiva: [
    {
      key: 'interceptions',
      abbreviation: 'INT',
      titleKey:
        'eventodeportivo_estadisticajuegos_defensiva_texto_titulo_topintercepciones',
    },
    {
      key: 'tackles',
      abbreviation: 'E',
      titleKey:
        'eventodeportivo_estadisticajuegos_defensiva_texto_titulo_topentradasganadas',
    },
  ],
  porteria: [
    {
      key: 'saves',
      abbreviation: 'PAR',
      titleKey:
        'eventodeportivo_estadisticajuegos_portero_texto_titulo_topparadas',
    },
    {
      key: 'clean_sheets',
      abbreviation: 'PAZ',
      titleKey:
        'eventodeportivo_estadisticajuegos_portero_texto_titulo_topporteriacero',
    },
  ],
  faltas: [
    {
      key: 'fouls',
      abbreviation: 'FC',
      titleKey:
        'eventodeportivo_estadisticajuegos_faltas_texto_titulo_faltascometidas',
    },
    {
      key: 'fouls_against',
      abbreviation: 'FR',
      titleKey:
        'eventodeportivo_estadisticajuegos_faltas_texto_titulo_faltasrecibidas',
    },
    {
      key: 'yellow_cards',
      abbreviation: 'TA',
      titleKey:
        'eventodeportivo_estadisticajuegos_faltas_texto_titulo_tarjetasamarillas',
    },
  ],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const WCStatistics = () => {
  const { isMobile } = useBreakpoint();
  const { statistics, loadStatistics } = useSports();
  const translations = useSelector(state => state.translations);
  const [activeGameType, setActiveGameType] = useState('ofensiva');
  const [activeStatKey, setActiveStatKey] = useState('goals');
  const [fullListStat, setFullListStat] = useState(null);
  const [subtabHidden, setSubtabHidden] = useState(false);
  const sectionRefs = useRef({});
  const stickyHeaderRef = useRef(null);
  const statTabsRef = useRef(null);
  const viewRef = useRef(null);
  const pendingScrollKey = useRef(null);
  const statTabsDesktopRef = useRef(null);
  const statTabsDesktopSentinelRef = useRef(null);
  const [isStatTabsDesktopStuck, setIsStatTabsDesktopStuck] = useState(false);
  const [, setSearchParams] = useSearchParams();

  useEffect(
    () => {
      loadStatistics();
    },
    [loadStatistics],
  );

  useEffect(() => {
    if (window.innerWidth >= 1024) return;
    const container = viewRef.current?.closest('.content_div');
    const mundial = viewRef.current?.closest('[data-role="mundial2026"]');
    if (!container) return;
    const handleScroll = () => {
      const scrolled = container.scrollTop > 0;
      setSubtabHidden(scrolled);
      if (mundial) {
        if (scrolled) mundial.setAttribute('data-standings-scrolled', '');
        else mundial.removeAttribute('data-standings-scrolled');
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (mundial) mundial.removeAttribute('data-standings-scrolled');
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const wrapper = statTabsDesktopRef.current;
    const sentinel = statTabsDesktopSentinelRef.current;
    if (!wrapper || !sentinel) return;

    const update = () => {
      setIsStatTabsDesktopStuck(
        sentinel.getBoundingClientRect().top < wrapper.getBoundingClientRect().top - 0.5,
      );
    };

    const scroller = sentinel.parentElement || window;
    scroller.addEventListener('scroll', update, { passive: true });
    update();
    return () => scroller.removeEventListener('scroll', update);
  }, [isMobile]);

  const statsData = statistics.data?.data || {};
  const orderedKeys = statistics.data?.orderedKeys || [];

  const sortByApiOrder = (statDefs, keys) =>
    statDefs.slice().sort((a, b) => {
      if (!keys.length) return 0;
      const ai = keys.indexOf(a.key);
      const bi = keys.indexOf(b.key);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

  const currentStats = sortByApiOrder(
    STATS_BY_GAME_TYPE[activeGameType] || [],
    orderedKeys,
  ).map(stat => ({
    ...stat,
    label: statsData[stat.key]?.name || '',
  }));

  // Scroll pendiente tras cerrar FullRankingList (refs no existen mientras está abierta)
  useEffect(() => {
    if (fullListStat !== null || !pendingScrollKey.current) return;
    const statKey = pendingScrollKey.current;
    pendingScrollKey.current = null;
    const ref = sectionRefs.current[statKey];
    if (!ref) return;
    const stickyEl = statTabsRef.current;
    const scrollContainer = getScrollableParent(ref);

    // Usamos el bottom real del statTabsSticky como referencia total del sticky stack
    const stickyBottom = stickyEl ? stickyEl.getBoundingClientRect().bottom : 0;
    const elementTop = ref.getBoundingClientRect().top;
    const targetScrollTop = scrollContainer.scrollTop + (elementTop - stickyBottom) - (window.innerWidth <= 768 ? 24 : 40);
    scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }, [fullListStat]);

  //ELIMINA PARAMS DE OTRAS VISTAS
  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('screen');
        next.delete('stage_code');
        next.delete('matchId');
        next.delete('countryId');
        return next;
      },
      { replace: true },
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Translations helpers
  // ---------------------------------------------------------------------------
  const t = (key, max) =>
    renderMaxLengthText(getTranslationEmptyOrNullKey(key, translations), max);

  const gameTabs = GAME_TYPE_DEFS.map(({ key, translationKey }) => ({
    key,
    label: t(translationKey, 19),
  }));

  const getStatTitle = stat => {
    if (!stat.titleKey) return renderMaxLengthText(`Top ${stat.label}`, 34);
    return t(stat.titleKey, 34) || renderMaxLengthText(`Top ${stat.label}`, 34);
  };

  const viewAllLabel = t(
    'eventodeportivo_estadisticajuegos_enlace_texto_vertop',
    35,
  );
  const playerColumnLabel = t(
    'eventodeportivo_estadisticajuegos_ataque_tabla_texto_titulo_jugador',
    20,
  );
  const noDataLabel = t(
    'eventodeportivo_estadisticajuegos_texto_nohaydatos',
    60,
  );

  // ---------------------------------------------------------------------------
  // Metrics
  // ---------------------------------------------------------------------------
  const { trackSelectContent, trackViewItem } = useMundialMetrics();

  const activeStat = currentStats.find(s => s.key === activeStatKey);
  const activeCategoryLabel = activeStat?.label || '';

  useMundialScrollTracker({
    pageTitleSegments: ['estadisticas', activeGameType, activeCategoryLabel],
    contentSubsection: 'estadisticas',
    moduloName: activeCategoryLabel,
  });

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleGameTypeChange = typeKey => {
    trackSelectContent({
      pageTitleSegments: ['estadisticas', typeKey],
      pageTitle: typeKey,
      contentSubsection: 'estadisticas',
      moduloName: '',
      interactionType: INTERACTION_TYPES.MAIN_TABS,
    });
    setActiveGameType(typeKey);
    setFullListStat(null);
    setSubtabHidden(false);
    const firstSortedStat = sortByApiOrder(
      STATS_BY_GAME_TYPE[typeKey] || [],
      orderedKeys,
    )[0];
    if (firstSortedStat) {
      setActiveStatKey(firstSortedStat.key);
      trackViewItem({
        categoryLabel: typeKey,
        subCategoryLabel: statsData[firstSortedStat.key]?.name || firstSortedStat.key,
      });
    }

    if (isMobile && stickyHeaderRef.current) {
      const scrollContainer = getScrollableParent(stickyHeaderRef.current);
      scrollContainer.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  const handleStatTabClick = statKey => {
    const stat = currentStats.find(s => s.key === statKey);
    const categoryLabel = stat?.label || statKey;
    trackSelectContent({
      pageTitleSegments: ['estadisticas', activeGameType, categoryLabel],
      pageTitle: categoryLabel,
      contentSubsection: 'estadisticas',
      moduloName: categoryLabel,
      interactionType: INTERACTION_TYPES.MAIN_TABS,
    });
    trackViewItem({
      categoryLabel: activeGameType,
      subCategoryLabel: categoryLabel,
    });
    setActiveStatKey(statKey);
    setFullListStat(null);

    if (isMobile) {
      if (fullListStat) {
        pendingScrollKey.current = statKey;
        return;
      }
      const ref = sectionRefs.current[statKey];
      if (ref) {
        const stickyEl = statTabsRef.current;
        const scrollContainer = getScrollableParent(ref);
        const stickyBottom = stickyEl ? stickyEl.getBoundingClientRect().bottom : 0;
        const elementTop = ref.getBoundingClientRect().top;
        const targetScrollTop = scrollContainer.scrollTop + (elementTop - stickyBottom) - (window.innerWidth <= 768 ? 24 : 40);
        scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      }
    }
  };

  // --- Desktop: single section or full list ---
  const renderDesktopContent = () => {
    if (fullListStat) {
      const stat = currentStats.find(s => s.key === fullListStat);
      return (
        <FullRankingList
          title={getStatTitle(stat)}
          statLabel={stat?.label}
          statAbbreviation={stat?.abbreviation}
          players={statsData[fullListStat]?.players || []}
          onBack={() => setFullListStat(null)}
          playerColumnLabel={playerColumnLabel}
        />
      );
    }

    const activeStat = currentStats.find(s => s.key === activeStatKey);
    const activePlayers = activeStat ? statsData[activeStat.key]?.players || [] : [];

    if (!statistics.fetched || activePlayers.length === 0) {
      return (
        <p className={styles.emptyMessage}>
          {statistics.loading ? '' : noDataLabel}
        </p>
      );
    }

    return (
      <div className={styles.sectionWrapper}>
        <div className={styles.highlight} />
        {activeStat && (
          <TopSection
            id={activeStat.key}
            title={getStatTitle(activeStat)}
            players={activePlayers}
            onViewAll={() => {
                setFullListStat(activeStat.key);
                window.scrollTo({ top: 0, behavior: 'instant' });
              }}
            viewAllLabel={viewAllLabel}
            showCountryName
          />
        )}
      </div>
    );
  };

  // --- Mobile: all sections scrollable ---
  const renderMobileContent = () => {
    if (fullListStat) {
      const stat = currentStats.find(s => s.key === fullListStat);
      return (
        <FullRankingList
          title={getStatTitle(stat)}
          statLabel={stat?.label}
          statAbbreviation={stat?.abbreviation}
          players={statsData[fullListStat]?.players || []}
          onBack={() => setFullListStat(null)}
          playerColumnLabel={playerColumnLabel}
        />
      );
    }

    if (!statistics.fetched) {
      return (
        <p className={styles.emptyMessage}>
          {statistics.loading ? '' : noDataLabel}
        </p>
      );
    }

    return currentStats.map(stat => {
      const players = statsData[stat.key]?.players || [];
      return (
        <div
          key={stat.key}
          ref={el => {
            sectionRefs.current[stat.key] = el;
          }}
          className={styles.mobileSectionGap}
        >
          {players.length === 0 ? (
            <p className={styles.emptyMessage}>{noDataLabel}</p>
          ) : (
            <MobileTopSection
              id={stat.key}
              title={getStatTitle(stat)}
              players={players}
              onViewAll={() => {
                setActiveStatKey(stat.key);
                setFullListStat(stat.key);
                const scrollContainer = getScrollableParent(viewRef.current);
                scrollContainer.scrollTo({ top: 0, behavior: 'instant' });
              }}
              viewAllLabel={viewAllLabel}
            />
          )}
        </div>
      );
    });
  };

  return (
    <WCBackground noPaddingBottom>
      <div ref={viewRef} className={styles.view} data-subtab-hidden={subtabHidden ? '' : undefined}>
        {/* GameTypeTabs: colapsan con scroll (subtabs) */}
        <div ref={stickyHeaderRef} className={classNames(styles.stickyHeader, { [styles['stickyHeader--hidden']]: subtabHidden })}>
          <GameTypeTabs
            tabs={gameTabs}
            activeKey={activeGameType}
            onChange={handleGameTypeChange}
          />
        </div>
        {/* StatTabs: sticky independiente debajo del main nav (sub-subtabs) */}
        {isMobile && (
          <div ref={statTabsRef} className={styles.statTabsSticky}>
            <StatTabs
              stats={currentStats}
              activeKey={activeStatKey}
              onTabClick={handleStatTabClick}
            />
          </div>
        )}

        <div className={styles.container}  data-role="wc-statistics-sections">
          {!isMobile && (
            <>
              <div ref={statTabsDesktopSentinelRef} />
              <div
                ref={statTabsDesktopRef}
                className={styles.statTabsDesktop}
                data-stuck={isStatTabsDesktopStuck ? '' : undefined}
              >
                <StatTabs
                  stats={currentStats}
                  activeKey={activeStatKey}
                  onTabClick={handleStatTabClick}
                />
              </div>
            </>
          )}

          <div className={styles.sections}>
            {isMobile ? renderMobileContent() : renderDesktopContent()}
          </div>
        </div>
      </div>
    </WCBackground>
  );
};

export default WCStatistics;
